import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useFilter } from '../../context/FilterContext';

// Default categories matching the brand UI
const DEFAULT_CATEGORIES = [
  { id: 'home', label: 'HOME', link: '/' },
  { id: 'b2b', label: 'GET B2B', link: '#exchanges' },
  { id: 'b2c', label: 'GET B2C', link: '#exchanges' },
  { id: 'getid', label: 'GET ID', link: '#get-id' },
  { id: 'whitelabel', label: 'WHITE LABEL', link: '#whitelabel' },
  { id: 'lineaccess', label: 'LINE ACCESS', link: '#lineaccess' },
  { id: 'joinplatform', label: 'JOIN PLATFORM', link: '#joinplatform' },
  { id: 'services', label: 'BUSINESS SERVICES', link: '#services' },
  { id: 'reviews', label: 'REVIEWS', link: '#reviews' },
  { id: 'casestudies', label: 'CASE STUDIES', link: '#cases' },
  { id: 'about', label: 'ABOUT', link: '#about' },
  { id: 'contact', label: 'CONTACT', link: '#contact' },
];

export function scrollToAnchor(target) {
  if (!target) return false;
  const id = target.startsWith('#') ? target.slice(1) : target;
  const el = document.getElementById(id) || document.querySelector(`[data-anchor="${id}"]`);
  if (!el) return false;
  const isMobile = window.innerWidth < 768;
  const offset = isMobile ? 140 : 110;
  requestAnimationFrame(() => {
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  });
  return true;
}

export default function Navigation() {
  const { data } = useSite();
  const { category, setCategory, setQuery } = useFilter();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const rawItems = data.navigation && data.navigation.length > 0 ? data.navigation : DEFAULT_CATEGORIES;
  
  // Ensure GET B2B, GET B2C, and GET ID are always in the navigation bar if not explicitly custom
  const items = (() => {
    let list = [...rawItems];
    const hasB2B = list.some(i => i.id === 'b2b' || i.label?.toLowerCase() === 'b2b' || i.label?.toLowerCase() === 'get b2b');
    const hasB2C = list.some(i => i.id === 'b2c' || i.label?.toLowerCase() === 'b2c' || i.label?.toLowerCase() === 'get b2c');
    const hasGetId = list.some(i => i.id === 'getid' || i.id === 'get-id' || i.label?.toLowerCase() === 'get id');

    if (!hasB2B || !hasB2C || !hasGetId) {
      const homeIdx = list.findIndex(i => i.id === 'home' || i.link === '/');
      const insertIdx = homeIdx >= 0 ? homeIdx + 1 : 0;
      const toInsert = [];
      if (!hasB2B) toInsert.push({ id: 'b2b', label: 'GET B2B', link: '#exchanges' });
      if (!hasB2C) toInsert.push({ id: 'b2c', label: 'GET B2C', link: '#exchanges' });
      if (!hasGetId) toInsert.push({ id: 'getid', label: 'GET ID', link: '#get-id' });
      list.splice(insertIdx, 0, ...toInsert);
    }
    return list;
  })();

  const active = category || 'home';

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 5);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5);
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [items]);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const offset = direction === 'left' ? -250 : 250;
    scrollRef.current.scrollBy({ left: offset, behavior: 'smooth' });
    setTimeout(checkScroll, 200);
  };

  const handleClick = (item) => {
    setQuery('');

    // Dedicated B2C redirect to Exchanges section
    if (item.id === 'b2c' || item.label?.toLowerCase() === 'b2c') {
      setCategory('b2c');
      const el = document.getElementById('exchanges') || document.getElementById('products');
      if (el) {
        const offset = window.innerWidth < 768 ? 140 : 110;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      }
      return;
    }

    // Dedicated B2B redirect to Exchanges section
    if (item.id === 'b2b' || item.label?.toLowerCase() === 'b2b') {
      setCategory('b2b');
      const el = document.getElementById('exchanges') || document.getElementById('products');
      if (el) {
        const offset = window.innerWidth < 768 ? 140 : 110;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      }
      return;
    }

    // Dedicated GET ID redirect to Get ID section
    if (item.id === 'getid' || item.id === 'get-id' || item.label?.toLowerCase() === 'get id') {
      setCategory('home');
      setTimeout(() => {
        const el = document.getElementById('get-id') || document.getElementById('exchanges') || document.getElementById('inplay');
        if (el) {
          const offset = window.innerWidth < 768 ? 140 : 110;
          const top = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
        }
      }, 50);
      return;
    }

    setCategory(item.id);
    if (item.link) {
      if (/^https?:/.test(item.link)) { window.open(item.link, '_blank'); return; }
      if (item.link === '/' || item.link === '') {
        requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
        return;
      }
      if (scrollToAnchor(item.link)) return;
    }

    requestAnimationFrame(() => {
      const target = document.getElementById('products')
                  || document.querySelector('[data-testid="gamecards-grid"], #platforms-showcase, #top-platforms');
      if (target) {
        const isMobile = window.innerWidth < 768;
        const offset = isMobile ? 140 : 110;
        const top = target.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  };

  const navBg = '#3e3c22';
  const baseNavBg = navBg;

  return (
    <div
      className="w-full border-b border-[#2d2b18] select-none transition-all duration-300 shadow-sm"
      style={{
        background: '#3e3c22',
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 2px 8px rgba(0, 0, 0, 0.3)'
      }}
      data-testid="top-navigation"
    >
      <div className="max-w-[1440px] mx-auto relative flex items-center px-1 sm:px-2 py-1.5 min-h-[40px] md:min-h-[42px]">
        
        {/* Left Scroll Arrow */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 h-7 w-7 flex items-center justify-start text-zinc-300 hover:text-white pl-0.5"
            style={{ background: `linear-gradient(to right, ${navBg} 0%, ${navBg} 70%, transparent 100%)` }}
            aria-label="Scroll left"
          >
            <ChevronLeft size={16} strokeWidth={2.5} />
          </button>
        )}

        {/* Categories Horizontal Scroll Row */}
        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex items-center gap-1 sm:gap-1.5 overflow-x-auto no-scrollbar scroll-smooth w-full px-1"
        >
          {items.map((item) => {
            const isActive = active === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleClick(item)}
                data-testid={`nav-${item.id}`}
                className={`h-7 md:h-8 px-2.5 sm:px-3 rounded text-[11px] sm:text-xs font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-150 active:scale-95 shrink-0 flex items-center justify-center gap-1.5 ${
                  isActive
                    ? 'bg-[#48494b] text-white shadow-xs font-black'
                    : 'text-[#d2d2d2] hover:text-white hover:bg-black/20'
                }`}
              >
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Right Scroll Arrow */}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 h-7 w-7 flex items-center justify-end text-zinc-400 hover:text-white pr-0.5"
            style={{ background: `linear-gradient(to left, ${navBg} 0%, ${navBg} 70%, transparent 100%)` }}
            aria-label="Scroll right"
          >
            <ChevronRight size={16} strokeWidth={2.5} />
          </button>
        )}
      </div>
    </div>
  );
}
